import React, { useEffect, useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import { Copy, KeyRound, RefreshCw } from 'lucide-react';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from './ui/dialog';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { ApiService } from '../utils/api';
import { useToast } from './Toast';

const ALPHABET = 'ABCDEFGHJKLMNPQRSTUVWXYZabcdefghjkmnpqrstuvwxyz23456789';

const generatePassword = () => {
  const bytes = new Uint32Array(10);
  window.crypto.getRandomValues(bytes);
  return Array.from(bytes, (n) => ALPHABET[n % ALPHABET.length]).join('');
};

/** Admin sets a team member's password; the member is signed out everywhere. */
const SetMemberPasswordDialog = ({ eventId, member, onClose }) => {
  const { showSuccess, showError } = useToast();
  const [password, setPassword] = useState('');
  const [saved, setSaved] = useState(false);

  useEffect(() => {
    setPassword(member ? generatePassword() : '');
    setSaved(false);
  }, [member]);

  const mutation = useMutation({
    mutationFn: () => ApiService.setTeamMemberPassword(eventId, member.memberId, password),
    onSuccess: () => {
      setSaved(true);
      showSuccess('Password updated. Share it with the member securely.');
    },
    onError: (error) => showError(error.message || 'Could not set the password'),
  });

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(`Login ID: ${member.email}\nPassword: ${password}`);
      showSuccess('Login details copied');
    } catch (_) {
      showError('Copy failed — select and copy the password manually');
    }
  };

  const tooShort = password.length < 8;

  return (
    <Dialog open={!!member} onOpenChange={() => !mutation.isPending && onClose()}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><KeyRound className="h-5 w-5" /> Set password</DialogTitle>
          <DialogDescription>
            {member?.name || member?.email} ({member?.email}) will be signed out on every device and must use this password.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-2 px-6 pb-2">
          <Label htmlFor="member-password">New password</Label>
          <div className="flex gap-2">
            <Input
              id="member-password"
              value={password}
              onChange={(e) => { setPassword(e.target.value); setSaved(false); }}
              autoComplete="new-password"
              className="font-mono"
              disabled={saved}
            />
            <Button
              type="button"
              variant="outline"
              onClick={() => { setPassword(generatePassword()); setSaved(false); }}
              aria-label="Generate another password"
              disabled={saved}
            >
              <RefreshCw className="h-4 w-4" />
            </Button>
          </div>
          {tooShort && <p className="text-xs text-red-600">At least 8 characters.</p>}
          {saved && (
            <p className="rounded-md bg-green-50 px-3 py-2 text-sm text-green-800">
              Saved. Copy the details now — the password isn't shown again.
            </p>
          )}
        </div>
        <DialogFooter className="flex-col-reverse gap-2 sm:flex-row">
          <Button variant="outline" onClick={onClose} disabled={mutation.isPending}>
            {saved ? 'Done' : 'Cancel'}
          </Button>
          {saved ? (
            <Button onClick={copy}><Copy className="mr-2 h-4 w-4" /> Copy login details</Button>
          ) : (
            <Button onClick={() => mutation.mutate()} disabled={tooShort || mutation.isPending}>
              {mutation.isPending ? 'Saving…' : 'Set password'}
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default SetMemberPasswordDialog;
